import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button, Card, Field, Input, Stack } from '@dental-staffing-platform/ui';
import { forgotPassword } from '../auth-client.js';

export interface ForgotPasswordProps {
  title?: string;
  /** Where “Back to sign in” goes. */
  loginHref?: string;
}

/** Request a platform password-reset email. */
export function ForgotPassword({
  title = 'Reset your password',
  loginHref = '/login',
}: ForgotPasswordProps) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      setMessage(await forgotPassword(email.trim()));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send reset email');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      style={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '100vh',
        padding: '1.5rem',
        background: 'var(--dsp-bg)',
      }}
    >
      <Card style={{ width: '100%', maxWidth: 400, padding: '2rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <img src="/dpp-logo.png" alt="Dental Purchasing Partners" className="dsp-login-logo" />
          <h1 style={{ fontSize: '1.2rem', fontWeight: 800, marginTop: '1rem' }}>{title}</h1>
          <p className="dsp-muted" style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
            Enter your email and we’ll send a reset link if an account exists.
          </p>
        </div>
        <form onSubmit={(e) => void onSubmit(e)}>
          <Stack gap={14}>
            <Field label="Email">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </Field>
            <Button type="submit" disabled={busy}>
              {busy ? 'Sending…' : 'Send reset link'}
            </Button>
            {message ? (
              <p className="dsp-muted" style={{ fontSize: '0.88rem' }}>
                {message}
              </p>
            ) : null}
            {error ? <span className="dsp-field__error">{error}</span> : null}
            <a
              href={loginHref}
              className="dsp-muted"
              style={{ fontSize: '0.88rem', textAlign: 'center' }}
            >
              Back to sign in
            </a>
          </Stack>
        </form>
      </Card>
    </main>
  );
}
